import { html } from "htm/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { getGpsEnabled, getLastHole, setGpsEnabled, setLastHole, setLastRound } from "../storage.js";
import { getIdentity, setAppMode } from "../identity.js";
import { navigate } from "../router.js";
import { loadCourse } from "../courseData.js";
import { resumeHoleIndex } from "../holeCursor.js";
import { loadHoleGeo } from "../mapData.js";
import { isOnHole } from "../../../shared/holePlan.js";
import { position, startWatching, stopWatching, unavailable } from "../gps.js";
import { requestWakeLock, releaseWakeLock } from "../wakeLock.js";
import { scoreTone } from "../util.js";
import { useChanged } from "../animation.js";
import { ConnBanner } from "../components/ConnBanner.js";
import { GreenDistance } from "../components/GreenDistance.js";
import { Icon } from "../components/Icon.js";
import { RulesPanel } from "../components/RulesPanel.js";
import { WolfPanel } from "../components/WolfPanel.js";
import {
  FULL_ROUND,
  HALF_ROUND,
  holesInPlay,
  holesPlayedCount,
} from "../../../shared/handicap.js";
import { scorableHoles } from "../../../shared/leaderboard.js";
import { formats } from "../../../shared/formats/index.js";
import type {
  Course,
  CoursePath,
  CourseShape,
  HoleGeo,
  Landmark,
  Round,
  TeeSet,
} from "../../../shared/types.js";
import { TERMS } from "../copy.js";

/**
 * What this round plays, in the words the card would use.
 *
 * "18 holes" was safe to hardcode until nine-hole courses existed; now `all18` means
 * "all of them" and only the tee set knows how many that is. A nine-hole course has no
 * front or back nine to name, so it just says nine holes whatever the round stored.
 */
function holeSetLabel(round: Round, teeSet: TeeSet | undefined): string {
  const played = holesPlayedCount(teeSet?.holes, round.holeSet);
  const card = teeSet?.holes.length ?? FULL_ROUND;
  if (card <= HALF_ROUND) return `${card} holes`;
  if (round.holeSet === "front9") return "front 9";
  if (round.holeSet === "back9") return "back 9";
  return `${played} holes`;
}

function Stepper({
  strokes,
  par,
  onSet,
  disabled = false,
}: {
  strokes: number | null;
  par: number;
  onSet: (v: number) => void;
  disabled?: boolean;
}) {
  // A short pop on change, so a tap registers at arm's length in bright sun without
  // having to read the number to know it took.
  const justSet = useChanged(strokes, 300);

  return html`
    <div class="score-stepper">
      <button
        class="secondary"
        disabled=${disabled}
        aria-label="One fewer stroke"
        onClick=${() => onSet(Math.max(1, (strokes ?? par) - 1))}
      >
        <${Icon} name="minus" size=${22} />
      </button>
      <span class=${`value ${scoreTone(strokes, par)} ${justSet ? "pop" : ""}`}>
        ${strokes ?? "–"}
      </span>
      <button
        class="secondary"
        disabled=${disabled}
        aria-label="One more stroke"
        onClick=${() => onSet((strokes ?? par - 1) + 1)}
      >
        <${Icon} name="plus" size=${22} />
      </button>
    </div>
  `;
}

/** Someone else's score: visible, but with no controls at all. Showing a disabled
 * stepper instead implies you could edit it if something changed, which is misleading —
 * these are never yours to touch. */
function ReadOnlyScore({ strokes }: { strokes: number | null }) {
  return html`<span class="score-readonly">${strokes ?? "–"}</span>`;
}

export function Scorecard({ code, roundId }: { code: string; roundId: string }) {
  const identity = getIdentity(code);
  const client = getClient(code);
  // Keyed by the course it was fetched for, so "still loading" is distinguishable from
  // "loaded and there is nothing there" — the two want very different messages, and
  // collapsing them showed "no course selected" for every round that had one.
  const [loaded, setLoaded] = useState<{ id: string; course: Course | null } | null>(null);
  // Where you are on the card, per round. Held as `{roundId, index}` rather than a bare
  // number because this screen isn't remounted on a round switch (see AppShell), so a
  // bare index would follow you from one round into the next; tagging it means a switch
  // falls through to the remembered hole for the round you switched *to*.
  const [cursor, setCursor] = useState<{ roundId: string; index: number } | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [gpsOn, setGpsOn] = useState(getGpsEnabled);
  // Keyed like `loaded` above, and for the same reason: a hole's geometry arriving
  // after the player has already moved on must not be shown against the new hole.
  const [holeGeo, setHoleGeo] = useState<{
    key: string;
    geo: HoleGeo | null;
    hazards: CourseShape[];
    paths: CoursePath[];
    landmarks: Landmark[];
    others: HoleGeo[];
  } | null>(null);

  // Reading .value here auto-subscribes this component to re-render on change —
  // @preact/signals hooks into Preact's render options, so this works with htm
  // just as it would with JSX.
  const snapshot = client?.snapshot.value ?? null;
  const round = snapshot?.tournament.rounds.find((r) => r.id === roundId) ?? null;

  useEffect(() => {
    requestWakeLock();
    return () => releaseWakeLock();
  }, []);

  // So the Scorecard tab brings you back to the round you were actually scoring.
  useEffect(() => {
    setLastRound(code, roundId);
  }, [code, roundId]);

  // `cancelled` because the round switcher makes A→B→A reachable in two taps, and
  // without it the first response can land after the second and leave this round
  // showing another course's pars.
  useEffect(() => {
    const courseId = round?.courseId;
    if (!courseId) return;
    let cancelled = false;
    loadCourse(courseId).then((course) => {
      if (!cancelled) setLoaded({ id: courseId, course });
    });
    return () => {
      cancelled = true;
    };
  }, [round?.courseId]);

  // Only this round's course counts. `loaded` survives a round switch (the screen isn't
  // remounted — see AppShell), so a stale entry would otherwise be read as this round's.
  const course = loaded && round && loaded.id === round.courseId ? loaded.course : null;
  const courseLoading = Boolean(round?.courseId) && (!loaded || loaded.id !== round?.courseId);

  const teeSet = course?.teeSets.find((t) => t.name === round?.teeSet);
  const holes = teeSet && round ? holesInPlay(teeSet.holes, round.holeSet) : [];

  // Where this round resumes when nothing on screen has moved yet — the hole it was
  // last left on, which is the whole point of remembering it: the scorecard is a tab,
  // and looking at the standings on the 16th used to come back to the 1st tee.
  //
  // Recomputed when the round's holes change rather than once on mount, because the
  // course arrives asynchronously: on the first renders there is nothing to match a
  // remembered hole number against. Deliberately *not* state — a stored hole and a
  // tapped one are the same answer to the same question, and keeping them in one place
  // is what stops the two racing on the way in.
  const resumeIndex = useMemo(
    () => resumeHoleIndex(holes, round ? getLastHole(code, round.id) : null),
    [code, round?.id, holes.length, holes[0]?.number],
  );
  const holeIndex = cursor && round && cursor.roundId === round.id ? cursor.index : resumeIndex;
  const setHoleIndex = (index: number) => setCursor({ roundId: round!.id, index });
  // The hole you were on is deliberately kept, but it is an index into *this* round's
  // holes — and rounds don't all have the same number. Switching from
  // hole 15 of an 18-hole round to a 9-hole one, or the organiser changing a round to a
  // front nine underneath you, would otherwise leave `currentHole` undefined: no hole
  // card, no prev/next buttons to escape with, and a "no course selected" message about
  // a round that has one.
  //
  // Resolved above the loading guards rather than below them because the geometry fetch
  // below is a hook, and a hook cannot live past an early return. Everything after the
  // guards reuses these same values.
  const safeIndex = holes.length > 0 ? Math.min(holeIndex, holes.length - 1) : 0;
  const currentHole = holes[safeIndex];

  // Written on every move rather than on leaving, because there is no reliable "leaving"
  // on a phone: the tab bar, the back gesture and the screen going to sleep with the app
  // killed behind it all have to resume in the same place.
  const currentHoleNumber = currentHole?.number;
  useEffect(() => {
    if (!round || currentHoleNumber == null) return;
    setLastHole(code, round.id, currentHoleNumber);
  }, [code, round?.id, currentHoleNumber]);

  // Geometry is fetched per hole rather than per course: a traced course is mostly
  // polygons, and a player only ever looks at one hole at a time. The map itself is
  // cached whole on first touch (mapData.ts), so this is one network call per round at
  // most, and none at all once it is in localStorage.
  const geoKey = round?.courseId && currentHole ? `${round.courseId}:${currentHole.number}` : null;
  useEffect(() => {
    if (!geoKey) return undefined;
    const [courseId, holeNumber] = geoKey.split(":");
    let cancelled = false;
    loadHoleGeo(courseId!, Number(holeNumber)).then(({ geo, hazards, paths, landmarks, others }) => {
      if (!cancelled) setHoleGeo({ key: geoKey, geo, hazards, paths, landmarks, others });
    });
    return () => {
      cancelled = true;
    };
  }, [geoKey]);

  const geo = holeGeo && holeGeo.key === geoKey ? holeGeo.geo : null;
  const courseHazards = holeGeo && holeGeo.key === geoKey ? holeGeo.hazards : [];
  const coursePaths = holeGeo && holeGeo.key === geoKey ? holeGeo.paths : [];
  const courseLandmarks = holeGeo && holeGeo.key === geoKey ? holeGeo.landmarks : [];
  const otherHoles = holeGeo && holeGeo.key === geoKey ? holeGeo.others : [];

  // A high-accuracy watch over a five-hour round is a real battery cost, so it runs
  // only where it can produce something: this hole has been traced, and this player has
  // asked for distances. Released on unmount like the wake lock above.
  const holeHasGeo = geo?.greenCenter != null;
  useEffect(() => {
    if (!holeHasGeo || !gpsOn) return undefined;
    startWatching();
    return () => stopWatching();
  }, [holeHasGeo, gpsOn]);

  // Read here rather than inside the component so this screen re-renders as the fix
  // moves, and so the meta row below knows whether a live yardage is on screen.
  const fix = position.value;
  const noFix = unavailable.value;
  // `isOnHole` matters here, not just having a fix: away from the course the strip shows
  // no yardage at all, and without this the card's own yardage would be hidden to make
  // room for a number that isn't there.
  const showingLive = Boolean(
    holeHasGeo && gpsOn && fix && geo && isOnHole(geo, fix.coords),
  );

  if (!identity) {
    return html`
      <div class="card">
        <p>${`You haven't joined this ${TERMS.event} yet.`}</p>
        <a href="#/join/${code}">Join with code ${code}</a>
      </div>
    `;
  }
  if (!snapshot || !round) {
    return html`
      <div class="hole-card skeleton" style="height:150px"></div>
      <div class="stack mt-3">
        ${[0, 1, 2, 3].map((i) => html`<div class="player-score-row skeleton" key=${i} style="height:70px"></div>`)}
      </div>
    `;
  }

  function setStrokes(playerId: string, strokes: number | null) {
    client!.submit({ type: "setScore", roundId: round!.id, playerId, hole: currentHole!.number, strokes });
  }

  function strokesFor(playerId: string): number | null {
    const entry = snapshot!.scores.find(
      (s) => s.roundId === round!.id && s.playerId === playerId && s.hole === currentHole?.number,
    );
    return entry?.strokes ?? null;
  }

  const format = formats[round.formatId];
  const nameOf = (id: string) => snapshot!.tournament.players.find((p) => p.id === id)?.name ?? id;

  // Segment bounds are positions within the round's hole set, so the current hole's
  // segment is found by index rather than by hole number — on a back nine, hole 12 is
  // position 3. Partners change between segments, which is exactly what the scorecard
  // has to make obvious.
  const segment = round.segments.find(
    (s) => safeIndex + 1 >= s.startHole && safeIndex + 1 <= s.endHole,
  );

  /** A single-ball team's score is stored against one canonical member (the first),
   * which is what teamGrossOnHole reads back. Teams are disjoint, so this can't
   * collide with the other side's entry. */
  const teamScoreHolder = (team: string[]) => team[0]!;

  // `canOrganize`, not "holds the admin token": an organiser who has switched to
  // playing gets a player's scorecard, which is the entire point of the switch. They
  // are still the organiser — the badge says so, and one tap puts the powers back.
  const isOrganiser = identity.canOrganize;
  const isSpectator = identity.role === "spectator";
  const playingOrganiser = identity.isOrganizer && !identity.canOrganize;
  const gameEnded = snapshot.tournament.status === "complete";
  const roundClosed = Boolean(round.locked);
  // The organiser can still correct a closed round (spec §3); everyone else is blocked.
  // This only hides the controls — applyOp on the server is what actually enforces it.
  //
  // Spectators are disabled unconditionally, and that has to be its own clause: the
  // rest of this only fires once a round is closed, so an *open* round would otherwise
  // hand live steppers to somebody whose every op the server refuses. A control that
  // always fails is worse than no control, which is the same argument ReadOnlyScore
  // makes for other people's scores.
  const entryDisabled = isSpectator || ((roundClosed || gameEnded) && !isOrganiser);

  /** Mirrors the server's rule so the UI doesn't offer controls that would be
   * refused: your own score always, a teammate's only where you share this hole's
   * side (single-ball formats store one score for the pair). */
  function canEdit(targetId: string): boolean {
    if (isSpectator) return false;
    if (isOrganiser) return true;
    const self = identity!.seatId;
    if (self === targetId) return true;
    if (!segment || !self) return false;
    return segment.teams.some((team) => team.includes(self) && team.includes(targetId));
  }

  // Closing early is legitimate — darkness, weather, a group that quits — so it's
  // allowed, but it makes the round final over the holes that were played, and that
  // shouldn't be a surprise. Confirm with the actual number outstanding.
  const scorable = course ? scorableHoles(round, course, snapshot.tournament.players, snapshot.scores) : [];
  const holesOutstanding = holes.length - scorable.length;

  function toggleLock() {
    if (!roundClosed && holesOutstanding > 0) {
      const ok = confirm(
        `${holesOutstanding} ${holesOutstanding === 1 ? "hole has" : "holes have"} no score yet. ` +
          `Closing the round now makes it final and scores it over the ${scorable.length} ` +
          `${scorable.length === 1 ? "hole" : "holes"} everyone finished. You can reopen it later.`,
      );
      if (!ok) return;
    }
    client!.submit({ type: "adminSetRoundLocked", roundId: round!.id, locked: !roundClosed });
  }

  // Which holes are fully in, for the progress rail — the same question `scorableHoles`
  // already answers, and it has to be asked the same way. Requiring a score from every
  // player in the game marked nothing on a scramble or alternate shot (one ball per
  // side, so only one member of each ever has a row) and nothing on any hole where
  // somebody sat the segment out, leaving the rail permanently blank on exactly the
  // rounds it was meant to help with.
  const scoredHoles = new Set(scorable);
  const holeComplete = (holeNumber: number) => scoredHoles.has(holeNumber);

  return html`
    <${ConnBanner} status=${client!.status.value} outboxSize=${client!.outboxSize.value} lastError=${client!.lastError.value} />

    <h1 class="mb-0">${round.name}</h1>
    <p class="course-line">
      ${course
        ? `${course.name}${teeSet ? ` · ${teeSet.name} tees` : ""} · ${holeSetLabel(round, teeSet)}`
        : "No course selected for this round"}
    </p>

    ${snapshot.tournament.rounds.length > 1 &&
    html`
      <label class="field mb-3">
        <span class="field-label">Round</span>
        <select
          value=${round.id}
          onChange=${(e: any) => navigate(`/t/${code}/round/${e.currentTarget.value}`)}
        >
          ${snapshot.tournament.rounds.map(
            (r) => html`
              <option key=${r.id} value=${r.id}>
                ${`${r.name || "Untitled round"}${r.locked ? " (closed)" : ""}`}
              </option>
            `,
          )}
        </select>
      </label>
    `}

    ${(roundClosed || gameEnded) &&
    html`
      <p class="closed-banner">
        ${gameEnded
          ? `This ${TERMS.event} has been ended by the ${TERMS.organizer}.`
          : `This round has been closed by the ${TERMS.organizer}.`}
        ${isOrganiser ? " You can still correct scores." : " Scores are final."}
      </p>
      ${playingOrganiser &&
      html`
        <!-- Their own doing, and reversible — but a screen that just withholds a power
             this person definitely has reads as a fault rather than as a choice. -->
        <p class="field-help mt-0">
          You're playing right now.${" "}
          <button class="linkish" onClick=${() => setAppMode(code, "organizer")}>
            ${`Switch back to ${TERMS.Organizer}`}
          </button>${" "}
          to correct this.
        </p>
      `}
    `}

    ${!currentHole
      ? courseLoading
        ? html`<div class="hole-card skeleton mt-3" style="height:150px"></div>`
        : html`<p class="notice mt-3">
            <${Icon} name="alert" size=${16} />${" "}
            ${!round.courseId
              ? `No course chosen for this round yet — the ${TERMS.organizer} sets that up.`
              : !course
                ? "This round's course couldn't be loaded. It'll appear once you're back online."
                : `No tee set chosen for this round yet — the ${TERMS.organizer} sets that up.`}
          </p>`
      : html`
          <div class="hole-card">
            <div class="hole-nav">
              <button
                class="secondary"
                disabled=${safeIndex === 0}
                aria-label="Previous hole"
                onClick=${() => setHoleIndex(Math.max(0, safeIndex - 1))}
              >
                <${Icon} name="left" size=${24} />
              </button>
              <div>
                <div class="hole-number">
                  <small>Hole</small>
                  ${currentHole.number}
                </div>
                <div class="hole-meta">
                  <span class="chip chip-accent">Par ${currentHole.par}</span>
                  <span class="chip">SI ${currentHole.strokeIndex}</span>
                  ${/* Card yardage — tee to green. It moves down into the distance
                       strip, relabelled, whenever a live you-to-green number is on
                       screen: two different yardages presented alike on one card get
                       misread. With no live distance it stays exactly where it was. */
                  currentHole.yards &&
                  !showingLive &&
                  html`<span class="chip">${currentHole.yards} yd</span>`}
                </div>
              </div>
              <button
                class="secondary"
                disabled=${safeIndex === holes.length - 1}
                aria-label="Next hole"
                onClick=${() => setHoleIndex(Math.min(holes.length - 1, safeIndex + 1))}
              >
                <${Icon} name="right" size=${24} />
              </button>
            </div>

            <${GreenDistance}
              geo=${geo}
              fix=${fix}
              enabled=${gpsOn}
              unavailable=${noFix}
              cardYards=${currentHole.yards}
              teeName=${teeSet?.name ?? null}
              hazards=${courseHazards}
              paths=${coursePaths}
              landmarks=${courseLandmarks}
              others=${otherHoles}
              onEnable=${() => {
                setGpsEnabled(true);
                setGpsOn(true);
              }}
            />

            <div
              class="hole-rail"
              role="img"
              aria-label=${`Hole ${safeIndex + 1} of ${holes.length}`}
            >
              ${holes.map(
                (h, i) => html`
                  <span
                    key=${h.number}
                    class=${i === safeIndex ? "here" : holeComplete(h.number) ? "done" : ""}
                  ></span>
                `,
              )}
            </div>
          </div>

          ${round.formatId === "wolf" &&
          course &&
          html`
            <${WolfPanel}
              round=${round}
              course=${course}
              players=${snapshot.tournament.players}
              scores=${snapshot.scores}
              perHoleData=${snapshot.perHoleData}
              hole=${currentHole.number}
              holeIndex=${safeIndex}
              totalHoles=${holes.length}
              disabled=${entryDisabled}
              onSet=${(data: any) =>
                client!.submit({
                  type: "setPerHole",
                  roundId: round!.id,
                  hole: currentHole!.number,
                  key: "wolf",
                  data,
                })}
            />
          `}

          ${segment
            ? html`
                <p class="segment-banner">
                  ${`Holes ${segment.startHole}-${segment.endHole} · `}
                  ${segment.teams.map((t) => t.map(nameOf).join(" & ")).join("  vs  ")}
                </p>
              `
            : format?.uses.teamPoints
              ? html`<p class="field-help">
                  No teams set for this round yet — add segments in setup so scores can be
                  entered by team.
                </p>`
              : null}

          <div class="stack mt-3">
            ${segment
              ? segment.teams.map((team, ti) => {
                  const teamName = team.map(nameOf).join(" & ");
                  if (format?.entry === "perTeam") {
                    // One ball per side, so one score per team — matching the format's
                    // own rules text ("One score per team per hole").
                    const holder = teamScoreHolder(team);
                    const strokes = strokesFor(holder);
                    return html`
                      <div class="player-score-row" key=${`team-${ti}`}>
                        <span class="who">${teamName}<small>team score</small></span>
                        ${canEdit(holder)
                          ? html`<${Stepper}
                              strokes=${strokes}
                              par=${currentHole!.par}
                              disabled=${entryDisabled}
                              onSet=${(v: number) => setStrokes(holder, v)}
                            />`
                          : html`<${ReadOnlyScore} strokes=${strokes} />`}
                      </div>
                    `;
                  }
                  return html`
                    <div class="team-group" key=${`team-${ti}`}>
                      <div class="team-heading">${teamName}</div>
                      ${team.map((id) => {
                        const strokes = strokesFor(id);
                        return html`
                          <div class="player-score-row" key=${id}>
                            <span class="who">${nameOf(id)}</span>
                            ${canEdit(id)
                              ? html`<${Stepper}
                                  strokes=${strokes}
                                  par=${currentHole!.par}
                                  disabled=${entryDisabled}
                                  onSet=${(v: number) => setStrokes(id, v)}
                                />`
                              : html`<${ReadOnlyScore} strokes=${strokes} />`}
                          </div>
                        `;
                      })}
                    </div>
                  `;
                })
              : snapshot.tournament.players.map((p) => {
                  const strokes = strokesFor(p.id);
                  return html`
                    <div class="player-score-row" key=${p.id}>
                      <span class="who">${p.name}</span>
                      ${canEdit(p.id)
                        ? html`<${Stepper}
                            strokes=${strokes}
                            par=${currentHole!.par}
                            disabled=${entryDisabled}
                            onSet=${(v: number) => setStrokes(p.id, v)}
                          />`
                        : html`<${ReadOnlyScore} strokes=${strokes} />`}
                    </div>
                  `;
                })}
          </div>
        `}

    <div class="stack mt-5">
      <button class="secondary" onClick=${() => setShowRules((v) => !v)}>
        <${Icon} name="book" size=${18} /> ${showRules ? "Hide" : "Show"} round rules
      </button>
      ${showRules &&
      html`<${RulesPanel}
        round=${round}
        players=${snapshot.tournament.players}
        teeSet=${teeSet}
      />`}

      ${isOrganiser &&
      html`
        <button class="ghost" onClick=${toggleLock}>
          ${roundClosed ? "Reopen this round" : "Close this round"}
        </button>
      `}
    </div>
  `;
}
